import React from 'react';
import classnames from 'classnames';
import { motion } from 'framer-motion';

// Hooks
import useCarousel from './useCarousel/useCarousel';
import { useIsMounted } from '../../hooks/useIsMounted';

// Components
import HeroBanner from '../HeroBanner';
import { ChevronCircleIcon } from '../Icons';

import './Carousel.scss';
export interface ICarouselProps {
  slides: any;
  isHero?: boolean;
  className?: string;
  onSlideChange?: (previousSlideIndex: number, nextSlideIndex: number) => void;
  analytics: any;
}

const Carousel = ({
  slides,
  isHero = true,
  className = '',
  onSlideChange,
  analytics,
}: ICarouselProps) => {
  const {
    currentItemIndexDesktop,
    setCurrentItemIndexDesktop,
    currentItemIndexMobile,
    setCurrentItemIndexMobile,
    carouselStepsRefDesktop,
    carouselStepsRefMobile,
    page,
    paginate,
    handleStepClick,
    ariaAnnouncement = '',
    shouldUseAnnouncement,
    setShouldUseAnnouncement,
    position,
    onPanEnd,
  } = useCarousel(slides, onSlideChange);

  const { isMounted } = useIsMounted();

  const renderSlide = (slide: any, i: number) => {
    if (!isHero) {
      return slide;
    }

    if (slide.fromStrapi) {
      return (
        <HeroBanner
          hidden={i !== page}
          trailer={slide.trailer}
          isCarouselSlide={true}
        >
          <HeroBanner.HeroImage
            heroImage={
              slide.image.imageFile.childImageSharp
                ? slide.image.imageFile
                : slide.image.url
            }
            imageAltText={slide.imageAltText || slide.title}
            isLiveEvent={slide.payPerView}
          />
          <HeroBanner.StrapiContent
            item={slide}
            analytics={analytics}
            assetType=""
          />
        </HeroBanner>
      );
    } else {
      return (
        <HeroBanner
          hidden={i !== page}
          trailer={slide.assetTrailer}
          isCarouselSlide={true}
        >
          <HeroBanner.HeroImage
            imageUrl={slide.imageUrl}
            imageAltText={slide.title}
          />
          <HeroBanner.MediaContent media={slide} analytics={analytics} />
        </HeroBanner>
      );
    }
  };

  const renderSlides = (slides: any) => {
    if (!slides.length || !isMounted) {
      return null;
    }

    if (slides.length === 1 && isHero) {
      const [slide] = slides;
      return renderSlide(slide, 0);
    }

    return slides.map((slide, i) => (
      <div
        key={i}
        aria-hidden={i !== page}
        role="group"
        aria-label={`${i + 1} of ${slides.length}`}
        className="slide"
      >
        {renderSlide(slide, i)}
      </div>
    ));
  };

  return (
    <section
      className={classnames({
        carousel: true,
        'carousel--solo': slides.length === 1,
        [`${className}`]: className,
        hero: isHero,
      })}
      aria-label="Featured Content"
    >
      <>
        <div
          className="visually-hidden"
          aria-live={shouldUseAnnouncement ? 'assertive' : 'off'}
          aria-hidden={!shouldUseAnnouncement}
        >
          {ariaAnnouncement}
        </div>

        {/* Carousel Steps - Desktop */}
        
      { slides.length > 1 && <div
          ref={carouselStepsRefDesktop}
          className="carousel__steps desktop"
          role="tablist"
          onFocus={() =>
            !currentItemIndexDesktop && setCurrentItemIndexDesktop(0)
          }
        >
          {slides.map((slide, i: number) => (
            <button
              key={i}
              tabIndex={currentItemIndexDesktop === null || page === i ? 0 : -1}
              aria-selected={page === i}
              role="tab"
              title={`${slide.title}`}
              aria-label={`${slide.title}`}
              className={`step ${page === i ? 'step--active' : ''}`}
              onClick={() => handleStepClick(i)}
              onFocus={() => setCurrentItemIndexDesktop(i)}
            />
          ))}
        </div>}

        {/* Carousel Steps - Mobile */}
       { slides.length > 1 && <div
          className="carousel__steps mobile"
          role="tablist"
          ref={carouselStepsRefMobile}
          onFocus={() =>
            !currentItemIndexMobile && setCurrentItemIndexMobile(0)
          }
        >
          {slides.map((slide, i: number) => (
            <button
              key={i}
              tabIndex={currentItemIndexMobile === null || page === i ? 0 : -1}
              aria-selected={page === i}
              role="tab"
              title={`${slide.title}`}
              aria-label={`${slide.title}`}
              className={`step ${page === i ? 'step--active' : ''}`}
              onClick={() => handleStepClick(i)}
              onFocus={() => setCurrentItemIndexMobile(i)}
            />
          ))}
        </div>}

        {/* Carousel Content */}
        <div
          className="carousel__slider-wrapper"
          style={{
            width: isHero ? '100vw' : '100%',
            height: !isMounted ? '100vh' : '100%',
          }}
        >
          <motion.div
            onPanEnd={onPanEnd}
            className="carousel__slider"
            animate={{ transform: `translateX(${position}%)` }}
            transition={{ type: 'spring', duration: 0.8, bounce: 0 }}
          >
            {renderSlides(slides)}
          </motion.div>
        </div>

        {/* Previous Button */}
        { slides.length > 1 && <button
          className="carousel__button"
          title="Previous Slide"
          aria-label="Previous Slide"
          onClick={() => {
            paginate(-1);
          }}
          onFocus={() => setShouldUseAnnouncement(true)}
          onBlur={() => setShouldUseAnnouncement(false)}
        >
          <ChevronCircleIcon direction="left" />
        </button> }
        

        {/* Next Button */}
       {slides.length > 1 && <button
          className="carousel__button"
          title="Next Slide"
          aria-label="Next Slide"
          onClick={() => {
            paginate(1);
          }}
          onFocus={() => setShouldUseAnnouncement(true)}
          onBlur={() => setShouldUseAnnouncement(false)}
        >
          <ChevronCircleIcon direction="right" />
        </button>}
      </>
    </section>
  );
};

export default Carousel;
